import type { ApiReturnPermissions } from '@highimpact-frontend/api-types';
import {
  createAsyncThunk,
  type ThunkAction,
  type UnknownAction,
} from '@reduxjs/toolkit';
import assert from 'assert';
import type { AxiosInstance, AxiosResponse } from 'axios';
import {
  api_config,
  api_permissions,
  api_semantic,
} from '../api/api-commons';
import { getSecurityToken } from '../utils/security';
import { keysMatching } from '../utils/type-generators';
import type { PermittedAction } from './data/data';
import { isByParams } from './data/data';
import type { DataStoreType, ExternalsWithParams } from './data/initials';
import {
  getKeyFromParamsAndId,
  setExternalState,
  setExternalStateWithParams,
  setPermissionState,
} from './data/slice';
import type { State, DataStateType } from './data/state';
import { isIdleState, isUnknownDataState } from './data/state';
import type { RootState } from './store';

interface QueryType<IdType, ValueType, TransformType = ValueType> {
  requirements: (keyof DataStoreType['externals'])[];

  setter: (
    id: IdType,
    value: State<IdType, ValueType>
  ) => { type: string; payload: unknown };
  transform: (response: AxiosResponse) => TransformType;

  request: (
    state: RootState,
    id: IdType
  ) => {
    api: AxiosInstance;
    route: string;
    method: 'get' | 'post';
    params: object;
  } | null;
}

const external_request_map: Record<
  keyof DataStoreType['externals'],
  () => { route: string; api: AxiosInstance }
> = {
  authentication: () => ({
    route: `/whoAmI/${getSecurityToken()}`,
    api: api_config,
  }),

  users: () => ({ route: '/users-highimpact', api: api_config }),
  roles: () => ({ route: '/roles', api: api_config }),
  projects: () => ({ route: '/projects', api: api_config }),
  sites: () => ({ route: '/sites', api: api_config }),
  actions: () => ({ route: '/actions', api: api_config }),
  devices: () => ({ route: '/devices', api: api_config }),
  measure_types: () => ({ route: '/measure_types', api: api_config }),
  measure_points: () => ({ route: '/measure-points', api: api_config }),
  campaigns: () => ({ route: '/campaigns', api: api_config }),
  companies: () => ({ route: '/companies', api: api_config }),
  device_types: () => ({ route: '/device-types', api: api_config }),
  thematics: () => ({ route: '/thematics', api: api_config }),
  resources_tags: () => ({ route: '/resources_tags/', api: api_config }),
  tags: () => ({ route: '/tags', api: api_config }),
  project_sites: () => ({ route: '/project_sites', api: api_config }),
  site_devices: () => ({ route: '/device_sites', api: api_config }),
  device_measure_types: () => ({
    route: '/device_measure-types',
    api: api_config,
  }),
  device_type_measure_types: () => ({
    route: '/device-type_measure-types',
    api: api_config,
  }),

  physical_quantities: () => ({
    route: '/get_physical_quantities',
    api: api_semantic,
  }),

  siteIdsToDashboardIds: () => ({
    route: '/sites/dashboards',
    api: api_config,
  }),
};

export interface DataLoadMapType {
  credentials: QueryType<
    keyof DataStoreType['externals'],
    PermittedAction
  >;
  externals: QueryType<
    keysMatching<DataStoreType['externals'], State<string, object>>,
    DataStoreType['externals'][keyof DataStoreType['externals']]
  >;
}

type DataLoadEntityType = keyof DataLoadMapType;

function isDataLoadEntityType(type: DataStateType): type is DataLoadEntityType {
  return type === 'credentials' || type === 'externals';
}

export const dataload_map: DataLoadMapType = {
  credentials: {
    requirements: [],
    setter: (external_key, value) =>
      setPermissionState({ external_key, value }),
    transform: (response: { data: ApiReturnPermissions }) => {
      return response.data.actions;
    },
    request: (_: RootState, external_key: string) => {
      return {
        api: api_permissions,
        method: 'get',
        params: {},
        route: `/permissions/?resource_name=${external_key}`,
      };
    },
  },

  externals: {
    requirements: [],

    setter: (external_key, value) =>
      setExternalState({
        external_key: external_key as keyof DataStoreType['externals'],
        value: value as DataStoreType['externals'][keyof DataStoreType['externals']],
      }),
    transform: (response) => {
      if (response.data instanceof Array) {
        let i = 0;

        return response.data.reduce((previous, current) => {
          previous[current.id || i++] = current;
          return previous;
        }, {});
      }

      return response.data;
    },

    request: (_state, external_key) => ({
      method: 'get',
      params: {},
      ...external_request_map[
        external_key as keysMatching<
          DataStoreType['externals'],
          State<string, object>
        >
      ](),
    }),
  },
};

export function requestData<IdType, ValueType>(
  entity: State<IdType, ValueType>
): ThunkAction<void, RootState, undefined, UnknownAction> {
  if (!isDataLoadEntityType(entity.type)) {
    return () => undefined;
  }

  const { type, id } = entity;

  const loader = dataload_map[type];

  const { requirements, setter, transform, request } = loader as QueryType<
    IdType,
    ValueType,
    unknown
  >;

  return async (dispatch, getState) => {
    const state = getState();

    const missings = requirements
      .map((key) => state.data_slice.externals[key])
      .filter((dependency) => !isByParams(dependency))
      .filter((dependency) => !isUnknownDataState(dependency));

    if (missings.length !== 0) {
      missings.forEach((missing) => {
        assert(!isUnknownDataState(missing));
        assert(!isByParams(missing));
        dispatch(requestData(missing));
      });
    } else if (isIdleState(entity)) {
      const query = request(state, id);

      if (!query) {
        return;
      }

      dispatch(setter(id, { _tag: 'loading', type, id }));

      try {
        const { api, method, route, params } = query;
        const response = await api[method](route, params);
        const transformed = transform(response) as ValueType;

        dispatch(
          setter(id, {
            _tag: 'data',
            data: transformed,
            type,
            id,
          })
        );
      } catch (error: unknown) {
        const message =
          error instanceof Error ? error.message : 'Unknown error';
        dispatch(setter(id, { _tag: 'error', message, type, id }));
      }
    }
  };
}

const getPathByParamsAndId = (params: object, id: ExternalsWithParams) => {
  switch (id) {
    case 'campaigns':
      return `/sites/${(params as { site_id: number }).site_id}/campaigns`;
  }
};

const getSearchParamsByParamsAndId = (
  _params: object,
  id: ExternalsWithParams
) => {
  switch (id) {
    case 'campaigns':
      return undefined;
  }
};

const getRequestFromParamsAndId = (params: object, id: ExternalsWithParams) => {
  const path = getPathByParamsAndId(params, id);
  const searchParams = getSearchParamsByParamsAndId(params, id);
  return () =>
    api_config.get(path, searchParams).then((response) => {
      if (response.data instanceof Array) {
        let i = 0;

        return response.data.reduce((previous, current) => {
          previous[current.id || i++] = current;
          return previous;
        }, {});
      }

      return response.data;
    });
};

export const requestDataWithParams = createAsyncThunk<
  void,
  { id: ExternalsWithParams; params: object }
>(
  'data/request-data-with-params',
  async (parameters, thunkAPI): Promise<void> => {
    const state = thunkAPI.getState() as RootState;
    const key = getKeyFromParamsAndId(parameters.params, parameters.id);
    const data = state.data_slice['externals'][parameters.id].value[key];
    const notLoaded = data === undefined || isIdleState(data);
    if (notLoaded) {
      thunkAPI.dispatch(
        setExternalStateWithParams({
          external_key: parameters.id,
          params: parameters.params,
          value: {
            _tag: 'loading',
            type: 'externals',
            id: parameters.id,
          },
        })
      );
      const performRequest = getRequestFromParamsAndId(
        parameters.params,
        parameters.id
      );
      performRequest()
        .then((data) =>
          thunkAPI.dispatch(
            setExternalStateWithParams({
              external_key: parameters.id,
              params: parameters.params,
              value: {
                _tag: 'data',
                data,
                type: 'externals',
                id: parameters.id,
              },
            })
          )
        )
        .catch((error) =>
          thunkAPI.dispatch(
            setExternalStateWithParams({
              external_key: parameters.id,
              params: parameters.params,
              value: {
                _tag: 'error',
                message: error.message,
                type: 'externals',
                id: parameters.id,
              },
            })
          )
        );
    }
  }
);
